'use client';
import React, { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { Menu, X, ChevronDown, LogOut, User } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const olympiadLinks = [
  { label: 'Mathematics', href: '/olympiad/mathematics' },
  { label: 'Science', href: '/olympiad/science' },
  { label: 'Chemistry', href: '/olympiad/chemistry' },
  { label: 'Physics', href: '/olympiad/physics' },
  { label: 'Biology', href: '/olympiad/biology' },
  { label: 'Computing & AI', href: '/olympiad/computing-ai' },
  { label: 'Astronomy', href: '/olympiad/astronomy' },
];

const navLinks = [
  { label: 'How It Works', href: '/#how-it-works' },
  { label: 'For Schools', href: '/#schools' },
  { label: 'Leaderboard', href: '/leaderboard' },
  { label: 'Blogs', href: '/blogs' },
];

export default function Topbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [olympiadOpen, setOlympiadOpen] = useState(false);
  const [mobileOlympiadOpen, setMobileOlympiadOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const router = useRouter();
  const { user, signOut } = useAuth();

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOlympiadOpen(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSignOut = async () => {
    try {
      await signOut();
      setUserMenuOpen(false);
      setMobileOpen(false);
      router.push('/');
      router.refresh();
    } catch {
      // ignore
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-card/95 backdrop-blur-md border-b border-border">
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link href="/" className="flex items-center gap-2.5">
          <AppLogo size={36} />
          <span className="font-extrabold text-lg text-primary tracking-tight">
            OnlineOlympiads<span className="text-secondary">.org</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {/* Olympiads Dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-primary rounded-lg hover:bg-muted transition-colors duration-150 flex items-center gap-1"
              onClick={() => setOlympiadOpen(!olympiadOpen)}
              aria-expanded={olympiadOpen}
            >
              Olympiads
              <ChevronDown size={14} className={`transition-transform duration-200 ${olympiadOpen ? 'rotate-180' : ''}`} />
            </button>
            {olympiadOpen && (
              <div className="absolute top-full left-0 mt-1 w-52 bg-card border border-border rounded-xl shadow-lg py-1.5 z-50">
                {olympiadLinks.map((link) => (
                  <Link
                    key={`dropdown-${link.label}`}
                    href={link.href}
                    className="block px-4 py-2.5 text-sm font-medium text-muted-foreground hover:text-primary hover:bg-muted transition-colors"
                    onClick={() => setOlympiadOpen(false)}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            )}
          </div>

          {navLinks.map((link) => (
            <Link
              key={`nav-${link.label}`}
              href={link.href}
              className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-primary rounded-lg hover:bg-muted transition-colors duration-150"
            >
              {link.label}
            </Link>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          {user ? (
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-foreground border border-border rounded-lg hover:bg-muted transition-colors duration-150"
              >
                <User size={15} />
                <span className="max-w-[120px] truncate">{user.user_metadata?.full_name || user.email?.split('@')[0] || 'Account'}</span>
                <ChevronDown size={13} className={`transition-transform duration-200 ${userMenuOpen ? 'rotate-180' : ''}`} />
              </button>
              {userMenuOpen && (
                <div className="absolute top-full right-0 mt-1 w-48 bg-card border border-border rounded-xl shadow-lg py-1.5 z-50">
                  <Link
                    href="/student-dashboard"
                    className="block px-4 py-2.5 text-sm font-medium text-muted-foreground hover:text-primary hover:bg-muted transition-colors"
                    onClick={() => setUserMenuOpen(false)}
                  >
                    Dashboard
                  </Link>
                  <button
                    onClick={handleSignOut}
                    className="w-full flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-red-500 hover:bg-red-50 transition-colors"
                  >
                    <LogOut size={14} />
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <>
              <Link
                href="/sign-up-login-screen"
                className="px-4 py-2 text-sm font-semibold text-primary border border-primary rounded-lg hover:bg-primary/5 transition-colors duration-150"
              >
                Sign In
              </Link>
              <Link
                href="/sign-up-login-screen"
                className="px-4 py-2 text-sm font-semibold text-primary-foreground bg-primary rounded-lg hover:bg-primary/90 transition-all duration-150 active:scale-95"
              >
                Register Free
              </Link>
            </>
          )}
        </div>

        <button
          className="lg:hidden p-2 rounded-lg hover:bg-muted transition-colors"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>
      {mobileOpen && (
        <div className="lg:hidden bg-card border-t border-border px-6 py-4 flex flex-col gap-2 fade-in">
          {/* Mobile Olympiads Accordion */}
          <button
            className="px-3 py-2.5 text-sm font-medium text-foreground hover:text-primary hover:bg-muted rounded-lg transition-colors flex items-center justify-between"
            onClick={() => setMobileOlympiadOpen(!mobileOlympiadOpen)}
          >
            Olympiads
            <ChevronDown size={14} className={`transition-transform duration-200 ${mobileOlympiadOpen ? 'rotate-180' : ''}`} />
          </button>
          {mobileOlympiadOpen && (
            <div className="pl-4 flex flex-col gap-1">
              {olympiadLinks.map((link) => (
                <Link
                  key={`mobile-olympiad-${link.label}`}
                  href={link.href}
                  className="px-3 py-2 text-sm text-muted-foreground hover:text-primary hover:bg-muted rounded-lg transition-colors"
                  onClick={() => setMobileOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          )}

          {navLinks.map((link) => (
            <Link
              key={`mobile-nav-${link.label}`}
              href={link.href}
              className="px-3 py-2.5 text-sm font-medium text-foreground hover:text-primary hover:bg-muted rounded-lg transition-colors"
              onClick={() => setMobileOpen(false)}
            >
              {link.label}
            </Link>
          ))}
          <div className="flex flex-col gap-2 pt-2 border-t border-border mt-2">
            {user ? (
              <>
                <Link
                  href="/student-dashboard"
                  className="px-4 py-2.5 text-sm font-semibold text-center text-primary border border-primary rounded-lg"
                  onClick={() => setMobileOpen(false)}
                >
                  Dashboard
                </Link>
                <button
                  onClick={handleSignOut}
                  className="px-4 py-2.5 text-sm font-semibold text-center text-red-500 border border-red-200 rounded-lg flex items-center justify-center gap-2"
                >
                  <LogOut size={14} />
                  Sign Out
                </button>
              </>
            ) : (
              <>
                <Link
                  href="/sign-up-login-screen"
                  className="px-4 py-2.5 text-sm font-semibold text-center text-primary border border-primary rounded-lg"
                  onClick={() => setMobileOpen(false)}
                >
                  Sign In
                </Link>
                <Link
                  href="/sign-up-login-screen"
                  className="px-4 py-2.5 text-sm font-semibold text-center text-primary-foreground bg-primary rounded-lg"
                  onClick={() => setMobileOpen(false)}
                >
                  Register Free
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}